import { isAcPowerTerminalId, isAcTerminalId } from "./ac";
import { TERMINAL_COLORS } from "./category-style";
import type { ComponentDefinition, Terminal, TerminalPolarity } from "./types";

/** AC identity belongs to the terminal id, as it does in the sizing engine.
 * A historical custom polarity edit must not turn an AC line into a DC + dot.
 * This is display-only: saved declarations still reach validation unchanged. */
export function terminalDisplayPolarity(terminal: Pick<Terminal, "id" | "polarity">): TerminalPolarity {
  if (!isAcTerminalId(terminal.id)) return terminal.polarity;
  if (/[-_]pe$/i.test(terminal.id)) return "ground";
  if (isAcPowerTerminalId(terminal.id)) return "neutral";
  return terminal.polarity === "ground" ? "ground" : "neutral";
}

export const US_AC_COLORS = { l: "#202020", l1: "#202020", l2: "#c43131", n: "#ffffff", pe: "#3e743e" } as const;

/** US 120 V line-to-neutral equipment, including 120/240 V split phase.
 * Display defaults only: never infer a DC rail from a conductor colour.
 * Hughes PWD-EPO-H installation guide uses black/red/white/green.
 */
export function acTerminalDefaultColor(
  terminal: Pick<Terminal, "id">,
  definition?: Pick<ComponentDefinition, "acVoltage">,
): string | undefined {
  if (definition?.acVoltage !== 120 || !isAcTerminalId(terminal.id)) return undefined;
  const pole = terminal.id.split(/[-_]/).at(-1)?.toLowerCase();
  return pole && Object.hasOwn(US_AC_COLORS, pole)
    ? US_AC_COLORS[pole as keyof typeof US_AC_COLORS] : undefined;
}

export function terminalDisplayColor(
  terminal: Pick<Terminal, "id" | "polarity" | "color">,
  definition?: Pick<ComponentDefinition, "acVoltage">,
): string {
  const customColor = (isAcTerminalId(terminal.id) || terminal.polarity === "signal") && terminal.color;
  return customColor || acTerminalDefaultColor(terminal, definition) || TERMINAL_COLORS[terminalDisplayPolarity(terminal)];
}

/** International conductor names stay recognizable even after a label edit. */
export function acTerminalDisplayLabel(terminalId: string): string | undefined {
  if (!isAcTerminalId(terminalId)) return undefined;
  const conductor = terminalId.split(/[-_]/).at(-1)?.toUpperCase();
  return ["L", "L1", "L2", "N", "PE"].includes(conductor ?? "") ? `AC ${conductor}` : "AC";
}
